/**
 * `Promise.all(items.map(worker))` with at most `concurrency` workers in
 * flight. Results keep the input order. A rejected worker rejects the whole
 * call, as `Promise.all` does; workers that catch their own errors keep the
 * rest of the items running.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workerCount = Math.max(
    1,
    Math.min(Math.floor(concurrency) || 1, items.length)
  );
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await worker(items[index] as T, index);
      }
    })
  );
  return results;
}
